"use client";
import { useMemo } from "react";
import { useTranslations } from "@zapier/i18n";
import { SortDescriptor, SortDirection } from "@zapier/design-system";
import { Table } from "@zapier/shared";

import AppRow from "./AppRow";
import { EmptyAppsTable } from "./EmptyAppsTable";
import { useShowEmptyAppsTable } from "./useShowEmptyAppsTable";
import { AppsModal } from "../types";
import { AppModals } from "../AppModals";
import {
  PublicAppWithImplementationsMetaAndStats,
  PrivateAppWithImplementationsMetaAndStats,
} from "@zapier/client";

import styles from "./AppsTable.module.css";

interface Props {
  allowsSorting?: boolean;
  ariaLabel: string;
  apps:
    | PublicAppWithImplementationsMetaAndStats[]
    | PrivateAppWithImplementationsMetaAndStats[];
  isLoading?: boolean;
  currentAppModal: AppsModal | undefined;
  onAddConnection: () => void;
  onSortChange?: (sortDescriptor: SortDescriptor) => void;
  setCurrentAppModal: (modal?: AppsModal | undefined) => void;
  sortDescriptor?: SortDescriptor;
  sortDisabledTooltip?: string;
}

const AppsTable = ({
  allowsSorting = false,
  apps,
  ariaLabel,
  currentAppModal,
  isLoading,
  onAddConnection,
  onSortChange,
  setCurrentAppModal,
  sortDescriptor: sortDescriptorProperty,
  sortDisabledTooltip,
}: Props) => {
  const t = useTranslations("apps-table");
  const columns = useMemo(() => {
    return [
      {
        label: t("columns.tags"),
        allowsSorting,
        sortKey: "tags",
        className: styles.appTagColumn,
      },
      {
        label: t("columns.expired-connections"),
        allowsSorting,
        sortKey: "has_stale_auth",
        className: styles.checkAppConnectionsColumn,
      },
      {
        label: t("columns.connections"),
        allowsSorting,
        sortKey: "auth_count",
        className: styles.connectionsCountColumn,
      },
      {
        label: t("columns.zaps"),
        allowsSorting,
        sortKey: "zap_count",
        className: styles.countColumn,
      },
      {
        label: t("columns.connection-users"),
        allowsSorting,
        sortKey: "member_count",
        className: styles.accessColumn,
      },
    ];
  }, [t, allowsSorting]);

  const sortDescriptor = sortDescriptorProperty || {
    column: "title",
    direction: "ascending" as SortDirection,
  };

  const emptyTableType = useShowEmptyAppsTable({
    isLoading,
    hasNoApps: !apps?.length,
  });

  if (emptyTableType) {
    return (
      <>
        <EmptyAppsTable
          tableType={emptyTableType}
          onAddConnection={onAddConnection}
        />
        <AppModals
          currentAppModal={currentAppModal}
          setCurrentAppModal={setCurrentAppModal}
        />
      </>
    );
  }

  return (
    <>
      <Table
        allowSortByTitle={allowsSorting}
        ariaLabel={ariaLabel}
        className={styles.appsTable}
        columns={columns}
        isLoading={isLoading}
        onSortChange={onSortChange}
        showCheckboxes={false}
        showOptions={false}
        showOwner={false}
        sortDescriptor={sortDescriptor}
        sortDisabledTooltip={sortDisabledTooltip}
      >
        {apps.map((app, index) => (
          <AppRow allowSelection={false} app={app} key={index} />
        ))}
      </Table>
      <AppModals
        currentAppModal={currentAppModal}
        setCurrentAppModal={setCurrentAppModal}
      />
    </>
  );
};
export default AppsTable;
